// COPIED from Remote-Leverage/hriq@3b50a234afdd packages/design-system/components/ui/v2/cell-row.tsx by apps/web/scripts/sync-design-system.mjs. Do not edit: change it in Hriq and re-sync.
// @ts-nocheck -- type-checked in Hriq under its own compiler options (the CRM's are stricter).
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@repo/design-system/components/ui/avatar";
import {
  TableCell,
  TableHead,
  TableRow,
} from "@repo/design-system/components/ui/table";
import { cn } from "@repo/design-system/lib/utils";
import { ArrowUpDown } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { Badge, type BadgeProps } from "./badge";
import { Button } from "./button";
import { Checkbox } from "./checkbox";

/**
 * v2 Cell + Row — the table-cell/table-row building blocks for the Figma
 * "Remote Leverage — Design System" file, Cell + Row component node
 * `392:44` ("Cell Text", "Cell Badge", "Cell Button", "Cell Profile",
 * "Cell Step", "Header Cell", "Documents Table Row", "Table Header",
 * "Status Indicator"). Composes v1 `table.tsx` (all Radix-free but still
 * the shared table/thead/tbody/tr/td primitives and their a11y semantics),
 * v2 `Badge` (status chips), v2 `Button` (row actions), and v2 `Checkbox`
 * (row selection — already Figma-accurate). `Avatar`/`AvatarImage`/
 * `AvatarFallback` are v1's own, unstyled: v2 Avatar isn't built yet (see
 * `ROADMAP.md`), and its default 32px size already matches this Figma
 * spec exactly, so no override is needed — per the ticket's own "use
 * Tailwind as a temp fallback" guidance, this is a plain v1 reuse, not a
 * new component.
 *
 * "Cell Input" is deliberately not implemented as a `Cell` type: neither
 * this ticket nor the roadmap row ("table.tsx (+ badge/avatar/button)")
 * lists Input as a composition target, and editable-cell value/onChange
 * semantics are a bigger, separate design decision than the other types.
 *
 * Rounded-corners-per-row requires `border-separate` + vertical
 * `border-spacing` on the wrapping v1 `<Table>` (real `<tr>` elements
 * can't carry a border-radius reliably) — so `Cell`, not `Row`, owns the
 * background/rounding/hover/selected treatment: each `Row` is a fully
 * transparent `<tr>`, and its `data-state=selected` / native `:hover`
 * cascade down to child `Cell`s via a `group` class. Usage:
 *
 *   <Table className="border-separate border-spacing-y-2">
 *     <TableHeader><HeaderRow>...</HeaderRow></TableHeader>
 *     <TableBody>
 *       <Row selected={row.id === selectedId}>
 *         <Cell type="profile" avatarSrc={row.avatar} avatarFallback="JW">{row.name}</Cell>
 *         <Cell type="badge" badgeColor="green">Verified</Cell>
 *       </Row>
 *     </TableBody>
 *   </Table>
 *
 * Selected-row tint (`--v2-cell-row-bg-selected`) has no Figma swatch in
 * this node — Figma's own screenshot shows no selected-state example —
 * so it's interpolated from the app's existing purple brand accent at low
 * opacity, the same way button.tsx interpolated icon-sm/icon-lg sizing:
 * documented as inferred, not lifted from a design.
 */

type CellType = "text" | "badge" | "profile" | "step" | "button";

type RowProps = ComponentProps<typeof TableRow> & {
  selected?: boolean;
};

function Row({ className, selected = false, ...props }: RowProps) {
  const dataState = selected ? "selected" : undefined;
  return (
    <TableRow
      className={cn(
        "ds-v2 group border-b-0 bg-transparent hover:bg-transparent data-[state=selected]:bg-transparent",
        className
      )}
      data-slot="cell-row"
      data-state={dataState}
      {...props}
    />
  );
}

function HeaderRow({ className, ...props }: ComponentProps<typeof TableRow>) {
  return (
    <TableRow
      className={cn(
        "ds-v2 border-b-0 bg-transparent hover:bg-transparent",
        className
      )}
      data-slot="cell-header-row"
      {...props}
    />
  );
}

type CellProps = Omit<ComponentProps<typeof TableCell>, "children"> & {
  type?: CellType;
  children?: ReactNode;
  badgeColor?: BadgeProps["color"];
  avatarSrc?: string;
  avatarFallback?: ReactNode;
  stepLabel?: string;
  /** 0–1 */
  stepProgress?: number;
  onAction?: () => void;
};

function Cell({
  className,
  type = "text",
  children,
  badgeColor = "gray",
  avatarSrc,
  avatarFallback,
  stepLabel,
  stepProgress = 0,
  onAction,
  ...props
}: CellProps) {
  return (
    <TableCell
      className={cn(
        "ds-v2 h-[60px] bg-card px-2 py-3 align-middle transition-colors",
        "first:rounded-l-[16px] last:rounded-r-[16px]",
        "group-hover:bg-(--v2-cell-row-bg-hover)",
        "group-data-[state=selected]:bg-(--v2-cell-row-bg-selected)",
        className
      )}
      data-slot="cell"
      data-type={type}
      {...props}
    >
      {type === "text" && (
        <span className="text-(--v2-cell-row-text-primary) text-sm">
          {children}
        </span>
      )}
      {type === "badge" && <Badge color={badgeColor}>{children}</Badge>}
      {type === "profile" && (
        <div className="flex items-center gap-2.5">
          <Avatar>
            {avatarSrc ? <AvatarImage alt="" src={avatarSrc} /> : null}
            <AvatarFallback>{avatarFallback}</AvatarFallback>
          </Avatar>
          <span className="text-(--v2-cell-row-text-primary) text-sm">
            {children}
          </span>
        </div>
      )}
      {type === "step" && (
        <div className="flex w-full max-w-[140px] flex-col items-start gap-1">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-(--v2-cell-row-step-track-bg)">
            <div
              className="h-full rounded-full bg-(--v2-cell-row-step-fill)"
              style={{
                width: `${Math.round(Math.min(1, Math.max(0, stepProgress)) * 100)}%`,
              }}
            />
          </div>
          {stepLabel ? (
            <span className="text-(--v2-cell-row-text-tertiary) text-xs">
              {stepLabel}
            </span>
          ) : null}
        </div>
      )}
      {type === "button" && (
        <Button onClick={onAction} size="sm" variant="outline">
          {children}
        </Button>
      )}
    </TableCell>
  );
}

type HeaderCellProps = Omit<ComponentProps<typeof TableHead>, "children"> & {
  type?: "text" | "checkbox";
  sortable?: boolean;
  checked?: boolean | "indeterminate";
  onCheckedChange?: (checked: boolean) => void;
  children?: ReactNode;
};

function HeaderCell({
  className,
  type = "text",
  sortable = true,
  checked,
  onCheckedChange,
  children,
  ...props
}: HeaderCellProps) {
  return (
    <TableHead
      className={cn(
        "ds-v2 h-10 px-2 py-3 font-medium text-foreground text-sm",
        type === "checkbox" ? "w-10 pr-1 pl-2" : null,
        className
      )}
      data-slot="cell-header"
      {...props}
    >
      {type === "checkbox" ? (
        <Checkbox checked={checked} onCheckedChange={onCheckedChange} />
      ) : (
        <span className="flex items-center gap-1.5">
          {children}
          {sortable ? (
            <ArrowUpDown className="size-4 text-(--v2-cell-row-icon-fg)" />
          ) : null}
        </span>
      )}
    </TableHead>
  );
}

type StatusDotProps = {
  active?: boolean;
  className?: string;
};

function StatusDot({ active = true, className }: StatusDotProps) {
  return (
    <span
      className={cn(
        "ds-v2 inline-block size-1.5 shrink-0 rounded-full ring-2 ring-white",
        active
          ? "bg-(--v2-cell-row-status-active)"
          : "bg-(--v2-cell-row-status-inactive)",
        className
      )}
      data-slot="cell-status-dot"
    />
  );
}

export { Row, HeaderRow, Cell, HeaderCell, StatusDot };
export type { CellType, RowProps, CellProps, HeaderCellProps, StatusDotProps };
